"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { HeaderLink , HeaderTargetDefense } from "@/.storyblok/types/319751/storyblok-components";
import HeaderBackground from "@/components/layouts/HeaderBackground";
import Image from "next/image";
import { SbBlokData } from "@storyblok/react";

interface HeaderProps {
  theme?: "light" | "dark";
  headerClass?: string;
  heroContent?: React.ReactNode;
  blok?: HeaderTargetDefense & SbBlokData;
}

const Header: React.FC<HeaderProps> = ({
  // theme = "dark",
  headerClass = "",
  heroContent,
  blok,
}) => {
  const navigationSections = (blok?.navigation_sections || []) as HeaderLink[];
  const halfIndex = Math.ceil(navigationSections.length / 2);

  const formatNavItems = (sections: HeaderLink[]) =>
    sections.map((section) => ({
      _uid: section._uid,
      label: section.title || section.label || "Unnamed Section",
      subMenu:
        (section.items as HeaderLink[])?.map((item: HeaderLink) => ({
          _uid: item._uid,
          label: item.label || "Unnamed Link",
          href: item.link?.cached_url || "#",
        })) ||
        (section.children as HeaderLink[])?.map((child: HeaderLink) => ({
          _uid: child._uid,
          label: child.label || "Unnamed Link",
          href: child.link?.cached_url || "#",
        })) ||
        [],
    }));


  const navItemsLeft = formatNavItems(navigationSections.slice(0, halfIndex));
  const navItemsRight = formatNavItems(navigationSections.slice(halfIndex));
  const navItems = formatNavItems(navigationSections);
  const buildLink = (item: any) =>
    item.label.includes("&")
      ? (() => {
          const [before, after] = item.label.split("&");
          return (
            <>
              {before.trim()} <br />
              {`& ${after.trim()}`}
            </>
          );
        })()
      : (() => {
          const parts = item.label.trim().split(" ");
          if (parts.length >= 2) {
            return (
              <>
                {parts[0]} <br /> {parts.slice(1).join(" ")}
              </>
            );
          } else {
            return item.label;
          }
        })();

  useEffect(() => {
    const initDropdowns = async () => {
      try {
        const bootstrap = await import("bootstrap");

        // Select all dropdown triggers
        const dropdownElementList = document.querySelectorAll(
          '[data-bs-toggle="dropdown"]'
        );

        // Initialize Bootstrap dropdowns
        const dropdownList = Array.from(dropdownElementList).map(
          (dropdownEl) => new bootstrap.Dropdown(dropdownEl)
        );

        // Store dropdowns for cleanup
        window._dropdowns = dropdownList;
      } catch (error) {
        console.error("Error initializing dropdowns:", error);
      }
    };

    if (typeof window !== "undefined") {
      initDropdowns();
    }

    // Cleanup Bootstrap dropdowns when the component unmounts
    return () => {
      if (window._dropdowns) {
        window._dropdowns.forEach((dropdown) => {
          if (dropdown && typeof dropdown.dispose === "function") {
            dropdown.dispose();
          }
        });
        window._dropdowns = [];
      }
    };
  }, []);

  const [menuExpanded, setMenuExpanded] = useState(false);

  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  const toggleMainMenu = () => {
    setMenuExpanded((prev) => !prev);
  };

  const toggleSubMenu = (uid: string) => {
    setExpandedMenu((prev) => (prev === uid ? null : uid));
  };

  const handleLinkClick = () => {
    setMenuExpanded(false);
    setExpandedMenu(null);
  };

  return (
    <div className={`header ${headerClass}`}>
      <HeaderBackground styleController={headerClass} />
      <div className="container position-relative z-index-1">
        <div className="header-inner">
          <div className="header-left">
            <nav>
              <ul className="nav">
                {navItemsLeft.map((item, index) => (
                  <li key={index}>
                    <a href="#" onClick={(e) => e.preventDefault()}>
                     {buildLink(item)}
                    </a>
                    <ul
                      className={
                        item.subMenu.length > 6
                          ? "nav-dropdown nav-dropdown-extended"
                          : "nav-dropdown"
                      }
                    >
                      {item.subMenu.map((sub: any, subIndex: any) => (
                        <li key={subIndex}>
                          <Link href={sub.href}>{sub.label}</Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="header-center">
            {blok?.logo_link && blok?.logo?.filename && (
              <Link href={blok.logo_link.cached_url || "/"}>
                <Image
                  src={blok.logo.filename}
                  alt={blok.logo.alt || "Target Defense Logo"}
                  title={blok.logo.title || "Target Defense"}
                  width={160}
                  height={94}
                  className="header-logo"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="eager"
                  quality={75}
                />
              </Link>
            )}
          </div>

          {/* Right Navigation */}
          <div className="header-right">
            <nav className="hidden-small-desktop">
              <ul className="nav justify-content-end">
                {navItemsRight.map((item, index) => (
                  <li key={index}>
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      {buildLink(item)}
                    </a>
                    <ul
                      className={
                        item.subMenu.length > 3
                          ? "nav-dropdown nav-dropdown-extended-sm"
                          : "nav-dropdown"
                      }
                    >
                      {item.subMenu.map((sub: any, subIndex: any) => (
                        <li key={subIndex}>
                          <Link href={sub.href}>{sub.label}</Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </nav>

            <nav className="mobile-only navbar-dark slicknav_menu">
              <button
                className="navbar-toggler slicknav_btn"
                type="button"
                aria-controls="menu"
                aria-expanded={menuExpanded ? "true" : "false"}
                aria-label="Toggle navigation"
                onClick={toggleMainMenu}
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              {/* Main Menu */}
              <ul
                className={`slicknav_nav navbar-nav ${
                  menuExpanded ? "show" : "collapse"
                }`}
                id="menu"
              >
                {/* Dynamic Menu Items */}
                {navItems.map((item) => (
                  <li
                    key={item._uid}
                    className={`slicknav_parent ${
                      expandedMenu === item._uid ? "slicknav_open" : ""
                    }`}
                  >
                    {item.subMenu && item.subMenu.length > 0 ? (
                      <>
                        {/* Parent Link */}
                        <a
                          href="#"
                          className="nav-link"
                          role="button"
                          aria-expanded={
                            expandedMenu === item._uid ? "true" : "false"
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            toggleSubMenu(item._uid); // Toggle submenu visibility
                          }}
                        >
                          {(item.label as string) || ""}
                        </a>
                        {/* Submenu */}
                        <ul
                          className={`${
                            expandedMenu === item._uid
                              ? "slicknav_collapsed"
                              : "slicknav_hidden d-none"
                          }`}
                        >
                          {item.subMenu.map(
                            (
                              sub: { href?: string; label: string },
                              subIndex: number
                            ) => (
                              <li key={subIndex}>
                                <Link
                                  href={sub.href || "#"}
                                  className="dropdown-item"
                                  onClick={handleLinkClick}
                                >
                                  {(sub.label as string) || ""}
                                </Link>
                              </li>
                            )
                          )}
                        </ul>
                      </>
                    ) : (
                      <Link
                        href="#"
                        className="nav-link"
                        onClick={handleLinkClick}
                      >
                        {(item.label as string) || ""}
                      </Link>
                    )}
                  </li>
                ))}

                <li className="slicknav_parent">
                  <Link
                    className="dropdown-item"
                    href="/about"
                    onClick={handleLinkClick}
                  >
                    About
                  </Link>
                </li>
                <li className="slicknav_parent">
                  <Link
                    className="dropdown-item"
                    href="/contact"
                    onClick={handleLinkClick}
                  >
                    Contact
                  </Link>
                </li>
                <li
                  className={`slicknav_parent ${
                    expandedMenu === "moreInformation" ? "slicknav_open" : ""
                  }`}
                >
                  <a
                    href="#"
                    className="nav-link"
                    role="button"
                    aria-expanded={
                      expandedMenu === "moreInformation" ? "true" : "false"
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      toggleSubMenu("moreInformation");
                    }}
                  >
                    More Information
                  </a>
                  {/* Static Submenu */}
                  <ul
                    className={`submenu ${
                      expandedMenu === "moreInformation"
                        ? "slicknav_collapsed"
                        : "slicknav_hidden d-none"
                    }`}
                  >
                    <li>
                      <Link
                        className="dropdown-item"
                        href="/soc"
                        onClick={handleLinkClick}
                      >
                        SOC
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        href="/partners"
                        onClick={handleLinkClick}
                      >
                        Partners
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        href="/resources"
                        onClick={handleLinkClick}
                      >
                        Resources
                      </Link>
                    </li>
                  </ul>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        {heroContent}
      </div>
    </div>
  );
};

// Add type declaration for window
declare global {
  interface Window {
    _dropdowns?: any[];
  }
}

export default Header;
